import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useSpring, useMotionValue, useTransform } from 'framer-motion';
import { Plus, Sparkles, ArrowUpRight, BarChart2, Calendar as CalendarIcon, Users, MessageSquare, ChevronRight, Cloud, Settings, Bookmark, Award, Target, Zap, Flame, TrendingUp, Star } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import WelcomeOverlay from '../components/WelcomeOverlay';
import { MetricCustomizeModal } from '../components/dashboard/MetricCustomizeModal';
import SubscriptionBadge from '../components/SubscriptionBadge';
import { getQuizResults } from '../lib/quiz';
import { useSocialMetrics } from '../lib/hooks/useSocialMetrics';
import { widgetDefinitions } from '../components/dashboard/widgets';

const Dashboard = () => {
  const [showWelcome, setShowWelcome] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState(getQuizResults());
  const [showMetricCustomize, setShowMetricCustomize] = useState(false);
  const [activeStreak, setActiveStreak] = useState(5); // Placeholder for active streak
  const [weeklyGoalProgress, setWeeklyGoalProgress] = useState(60); // Placeholder for weekly goal progress
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['total_posts', 'engagement', 'followers', 'views']);
  const { metrics, loading: metricsLoading } = useSocialMetrics();

  // Animation values for metrics
  const countSpring = useSpring(0, { stiffness: 100, damping: 30 });
  const countDisplay = useMotionValue(0);
  const countTransform = useTransform(countDisplay, Math.round);

  useEffect(() => {
    // Check if this is a new login session
    const lastLoginTime = localStorage.getItem('lastLoginTime');
    const currentTime = new Date().getTime();
    
    // Show welcome overlay if:
    // 1. No last login time exists, or
    // 2. Last login was more than 24 hours ago
    if (!lastLoginTime || currentTime - parseInt(lastLoginTime) > 24 * 60 * 60 * 1000) {
      setShowWelcome(true);
      localStorage.setItem('lastLoginTime', currentTime.toString());
    }
  }, []);

  useEffect(() => {
    loadMetricPreferences();
  }, []);

  const loadMetricPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if profile exists
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (profileError) {
        // Create profile if it doesn't exist
        const { error: insertProfileError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            full_name: user.user_metadata?.full_name || '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertProfileError) throw insertProfileError;
      }

      const { data, error } = await supabase
        .from('dashboard_metric_preferences')
        .select('metrics')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching preferences:', error);
        return;
      }

      // If no preferences exist yet, create default preferences
      if (!data) {
        // Create default preferences
        const { error: insertError } = await supabase
          .from('dashboard_metric_preferences')
          .insert({
            user_id: user.id,
            metrics: ['total_posts', 'engagement', 'followers', 'views']
          });

        if (insertError) {
          console.error('Error creating default preferences:', insertError);
          throw insertError;
        }
      } else {
        setSelectedMetrics(data.metrics);
      }
    } catch (err) {
      console.error('Error loading metric preferences:', err);
    }
  };

  const handleSaveMetrics = (newMetrics: string[]) => {
    // Ensure we always have exactly 4 metrics
    let metricsToSave = [...newMetrics];
    
    // If we have more than 4, trim to 4
    if (metricsToSave.length > 4) {
      metricsToSave = metricsToSave.slice(0, 4);
    }
    
    // If we have less than 4, add defaults until we have 4
    const defaultMetrics = ['total_posts', 'engagement', 'followers', 'views'];
    while (metricsToSave.length < 4) {
      for (const metric of defaultMetrics) {
        if (!metricsToSave.includes(metric)) {
          metricsToSave.push(metric);
          break;
        }
      }
    }
    
    setSelectedMetrics(metricsToSave);
  };

  // Get user's name from Supabase auth
  const userFullName = JSON.parse(localStorage.getItem('sb-bqpssdvpdwxpsjhkdjoc-auth-token') || '{}')
    ?.user?.user_metadata?.full_name || 'User';
  
  // Extract first name for more personal greeting
  const userName = userFullName.split(' ')[0];

  const metricOptions = {
    total_posts: {
      title: 'Total Posts',
      value: metricsLoading ? '-' : metrics.totalPosts.toLocaleString(),
      change: `${metrics.weeklyChange.posts >= 0 ? '+' : ''}${metrics.weeklyChange.posts}`,
      icon: MessageSquare,
      color: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
      link: '/library'
    },
    engagement: {
      title: 'Engagement',
      value: metricsLoading ? '-' : metrics.totalEngagement.toLocaleString(),
      change: `${metrics.weeklyChange.engagement >= 0 ? '+' : ''}${metrics.weeklyChange.engagement}`,
      icon: BarChart2,
      color: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
      link: '/performance'
    },
    followers: {
      title: 'Followers',
      value: metricsLoading ? '-' : metrics.totalFollowers.toLocaleString(),
      change: `${metrics.weeklyChange.followers >= 0 ? '+' : ''}${metrics.weeklyChange.followers}`,
      icon: Users,
      color: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
      link: '/contacts'
    },
    views: {
      title: 'Views',
      value: metricsLoading ? '-' : metrics.totalViews.toLocaleString(),
      change: `${metrics.weeklyChange.views >= 0 ? '+' : ''}${metrics.weeklyChange.views}`,
      icon: CalendarIcon,
      color: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
      link: '/calendar'
    },
    ctr: {
      title: 'Click-Through Rate',
      value: '4.2%',
      change: '+0.5%',
      icon: ArrowUpRight,
      color: 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400',
      link: '/performance'
    },
    reach: {
      title: 'Reach',
      value: '45.2K',
      change: '+15.3%',
      icon: Users,
      color: 'bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400',
      link: '/performance'
    },
    story_replies: {
      title: 'Story Replies',
      value: '128',
      change: '+24%',
      icon: MessageSquare,
      color: 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400',
      link: '/messaging'
    },
    saves: {
      title: 'Saves',
      value: '342',
      change: '+18%',
      icon: Bookmark,
      color: 'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400',
      link: '/performance'
    }
  };

  const selectedMetricStats = selectedMetrics.map(id => metricOptions[id as keyof typeof metricOptions]);
  const validMetricStats = selectedMetricStats.filter(stat => stat !== undefined);

  // Get widget components
  const WelcomeWidget = widgetDefinitions.find(w => w.type === 'welcome')?.component || (() => <div>Welcome Widget</div>);
  const AnalyticsWidget = widgetDefinitions.find(w => w.type === 'analytics')?.component || (() => <div>Analytics Widget</div>);
  const ActivityWidget = widgetDefinitions.find(w => w.type === 'activity')?.component || (() => <div>Activity Widget</div>);
  const CalendarWidget = widgetDefinitions.find(w => w.type === 'calendar')?.component || (() => <div>Calendar Widget</div>);
  const NewsWidget = widgetDefinitions.find(w => w.type === 'news')?.component || (() => <div>News Widget</div>);
  const TasksWidget = widgetDefinitions.find(w => w.type === 'tasks')?.component || (() => <div>Tasks Widget</div>);

  return (
    <>
      <AnimatePresence>
        {showWelcome && (
          <WelcomeOverlay
            userName={userName}
            onComplete={() => setShowWelcome(false)}
          />
        )}
      </AnimatePresence>

      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 overflow-x-hidden w-full max-w-[100vw]">
        <div className="w-full max-w-[100vw] sm:max-w-[1920px] mx-auto py-8 overflow-x-hidden mobile-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="space-y-8 overflow-x-hidden w-full"
          >
            {/* Dashboard Header */}
            <div className="flex flex-col items-center text-center space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              <div className="w-full">
                <div className="flex items-center justify-center space-x-3">
                  <motion.h1 
                    className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    Dashboard
                  </motion.h1>
                  <SubscriptionBadge />
                </div>
                <motion.p
                  className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400 text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  Welcome back, <span className="font-medium text-indigo-600 dark:text-indigo-400">{userName}</span>! Here's an overview of your content performance.
                </motion.p>
              </div>
              <div className="flex flex-row items-center justify-center space-x-4 w-full">
                <motion.button
                  whileHover={{ 
                    scale: 1.05, 
                    boxShadow: "0 0 15px rgba(99, 102, 241, 0.4)" 
                  }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowMetricCustomize(true)}
                  className="flex items-center space-x-2 px-3 sm:px-4 py-2 sm:py-2.5 border border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-all duration-200 w-auto"
                >
                  <Settings className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-1.5" />
                  <span className="text-sm">Customize Dashboard</span>
                </motion.button>
                <motion.div
                  whileHover={{ 
                    scale: 1.05, 
                    boxShadow: "0 0 15px rgba(99, 102, 241, 0.4)" 
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="w-auto"
                >
                  <Link
                    to="/library"
                    className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-md"
                  >
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-1.5" />
                    <span className="text-sm">Create Content</span>
                  </Link>
                </motion.div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-6 sm:mb-8 w-full">
              {selectedMetrics.map((metricId, index) => {
                const stat = metricOptions[metricId as keyof typeof metricOptions];
                if (!stat) return null;
                const Icon = stat.icon;
                return (
                  <motion.div 
                    key={stat.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ 
                      y: -5, 
                      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" 
                    }}
                    className="relative overflow-hidden bg-white dark:bg-dark-800 rounded-xl p-3 sm:p-5 shadow-md border border-gray-100 dark:border-dark-700 transition-all duration-200"
                  >
                    {/* Glassmorphism background effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-white/20 dark:from-dark-800/60 dark:to-dark-800/20 backdrop-blur-sm z-0"></div>
                    
                    {/* Subtle animated gradient background */}
                    <div className="absolute inset-0 opacity-10 bg-gradient-to-br from-indigo-200 via-purple-200 to-pink-200 dark:from-indigo-900 dark:via-purple-900 dark:to-pink-900 z-0 animate-pulse-slow"></div>
                    
                    <div className="flex items-center justify-between">
                      <div className="relative z-10">
                        <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">{stat.title}</p>
                        <motion.p
                          className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mt-1 sm:mt-1.5"
                          initial={{ scale: 0.9 }}
                          animate={{ scale: 1 }}
                          transition={{ 
                            type: "spring", 
                            stiffness: 300, 
                            damping: 20,
                            delay: index * 0.1 + 0.2
                          }}
                        >
                          {stat.value}
                        </motion.p>
                      </div>
                      <motion.div 
                        className={`p-2 sm:p-3 rounded-lg relative z-10 ${stat.color}`}
                        whileHover={{ rotate: 15, scale: 1.1 }}
                        transition={{ type: "spring", stiffness: 300, damping: 10 }}
                      >
                        <Icon className="h-4 w-4 sm:h-5 sm:w-5 drop-shadow-md" />
                      </motion.div>
                    </div>
                    <div className="mt-2 sm:mt-3 flex items-center justify-between relative z-10">
                      <motion.p 
                        className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400 flex items-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.1 + 0.4 }}
                      >
                        <TrendingUp className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                        {stat.change} this week
                      </motion.p>
                      <Link to={stat.link} className="text-[10px] sm:text-xs font-medium text-indigo-600 dark:text-indigo-400 flex items-center hover:underline">
                        Details <ArrowUpRight className="ml-1 h-3 w-3" />
                      </Link>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Main Content Area */}
            <div className="flex flex-col space-y-4 sm:space-y-6 w-full">
              {/* Welcome Widget - Full Width */}
              <motion.div 
                className="bg-white dark:bg-dark-800 rounded-xl shadow-lg border border-gray-100 dark:border-dark-700 p-3 sm:p-6 w-full overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <WelcomeWidget id="welcome" />
              </motion.div>
              
              {/* Goal Tracker and Streaks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 w-full">
                {/* Goal Tracker */}
                <motion.div 
                  className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg p-4 sm:p-6 text-white overflow-hidden relative"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  whileHover={{ y: -5 }}
                >
                  {/* Decorative elements */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -translate-x-1/4 -translate-y-1/4"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-white opacity-10 rounded-full translate-x-1/4 translate-y-1/4"></div>
                  
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-lg">
                      <Target className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">Weekly Engagement Goal</h3>
                      <p className="text-sm sm:text-base text-indigo-100 mb-3 sm:mb-4">You're {weeklyGoalProgress}% toward your weekly engagement goal!</p>
                      
                      {/* Progress bar */}
                      <div className="w-full h-2 sm:h-3 bg-white/20 rounded-full overflow-hidden mb-1 sm:mb-2">
                        <motion.div 
                          className="h-full bg-white"
                          initial={{ width: 0 }}
                          animate={{ width: `${weeklyGoalProgress}%` }}
                          transition={{ delay: 0.5, duration: 1, ease: "easeOut" }}
                        />
                      </div>
                      
                      <div className="flex justify-between text-[10px] sm:text-xs text-indigo-100">
                        <span>Current: 1,240</span>
                        <span>Goal: 2,000</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
                
                {/* Streaks and Badges */}
                <motion.div 
                  className="bg-white dark:bg-dark-800 rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-dark-700"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center">
                      <Award className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-amber-500" />
                      Achievements
                    </h3>
                    <Link to="/performance" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">View all</Link>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 sm:p-4 flex items-center space-x-2 sm:space-x-3">
                      <div className="p-1.5 sm:p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                        <Flame className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{activeStreak} Day Streak</div>
                        <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">Keep it going!</div>
                      </div>
                    </div>
                    
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3 sm:p-4 flex items-center space-x-2 sm:space-x-3">
                      <div className="p-1.5 sm:p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-full">
                        <Star className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div>
                        <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">Top Creator</div>
                        <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">Top 5% this week</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              {/* Analytics Widget */}
              <motion.div 
                className="bg-white dark:bg-dark-800 rounded-xl shadow-lg border border-gray-100 dark:border-dark-700 p-3 sm:p-6 w-full"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                whileHover={{ y: -5 }}
              >
                <AnalyticsWidget id="analytics" />
              </motion.div>

              {/* Calendar Widget */}
              <motion.div 
                className="bg-white dark:bg-dark-800 rounded-xl shadow-lg border border-gray-100 dark:border-dark-700 p-3 sm:p-6 w-full"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                whileHover={{ y: -5 }}
              >
                <CalendarWidget id="calendar" />
              </motion.div>

              {/* News and Tasks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 w-full">
                {/* News Widget */}
                <motion.div 
                  className="bg-white dark:bg-dark-800 rounded-xl shadow-lg border border-gray-100 dark:border-dark-700 p-3 sm:p-6 w-full"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  whileHover={{ y: -5 }}
                >
                  <NewsWidget id="news" />
                </motion.div>

                {/* Tasks Widget */}
                <motion.div 
                  className="bg-white dark:bg-dark-800 rounded-xl shadow-lg border border-gray-100 dark:border-dark-700 p-3 sm:p-6 w-full"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                  whileHover={{ y: -5 }}
                >
                  <TasksWidget id="tasks" />
                </motion.div>
              </div>

              {/* Recommended Actions */}
              <motion.div 
                className="bg-white dark:bg-dark-800 rounded-xl shadow-lg border border-gray-100 dark:border-dark-700 p-3 sm:p-6 w-full"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0 }}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-center justify-between mb-3 sm:mb-5">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2 sm:mb-4 flex items-center">
                    <Zap className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-indigo-600 dark:text-indigo-400" />
                    Recommended Actions
                  </h3>
                  <button className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline">View All</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5 group w-full">
                  <motion.div 
                    className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/30 rounded-xl p-3 sm:p-5 border border-indigo-100 dark:border-indigo-800/30 shadow-sm hover:shadow-md transition-all duration-300 group-hover:opacity-90 hover:!opacity-100"
                    whileHover={{ y: -5, scale: 1.02 }}
                  >
                    <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                      <div className="p-1.5 sm:p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                        <Users className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <h4 className="text-sm sm:text-base font-bold text-indigo-900 dark:text-indigo-100">Complete Your Profile</h4>
                    </div>
                    <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-indigo-700 dark:text-indigo-300">Add your social media accounts to enable cross-posting.</p>
                    <Link to="/settings" className="mt-2 sm:mt-4 flex items-center text-xs sm:text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors">
                      Go to Settings <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  </motion.div>
                  <motion.div 
                    className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/30  rounded-xl p-3 sm:p-5 border border-amber-100  dark:border-amber-800/30 shadow-sm hover:shadow-md transition-all duration-300 group-hover:opacity-90 hover:!opacity-100"
                    whileHover={{ y: -5, scale: 1.02 }}
                  >
                    <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                      <div className="p-1.5 sm:p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                        <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <h4 className="text-sm sm:text-base font-bold text-amber-900 dark:text-amber-100">Schedule Your Content</h4>
                    </div>
                    <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-amber-700 dark:text-amber-300">You have 3 drafts ready to be scheduled for posting.</p>
                    <Link to="/calendar" className="mt-2 sm:mt-4 flex items-center text-xs sm:text-sm font-medium text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 transition-colors">
                      Go to Calendar <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  </motion.div>
                  <motion.div 
                    className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/30 rounded-xl p-3 sm:p-5 border border-green-100 dark:border-green-800/30 shadow-sm hover:shadow-md transition-all duration-300 group-hover:opacity-90 hover:!opacity-100"
                    whileHover={{ y: -5, scale: 1.02 }}
                  >
                    <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                      <div className="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <BarChart2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <h4 className="text-sm sm:text-base font-bold text-green-900 dark:text-green-100">Review Analytics</h4>
                    </div>
                    <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-green-700 dark:text-green-300">Your latest post is performing 25% better than average.</p>
                    <Link to="/performance" className="mt-2 sm:mt-4 flex items-center text-xs sm:text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 transition-colors">
                      View Performance <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Metric Customization Modal */}
          <MetricCustomizeModal
            isOpen={showMetricCustomize}
            onClose={() => setShowMetricCustomize(false)}
            onSave={handleSaveMetrics}
            currentMetrics={selectedMetrics}
          />
        </div>
      </div>
    </>
  );
};

export default Dashboard;